import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Camera, Heart, Zap, MessageCircle, Users, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import { Sticker } from "@/components/Stickers";
import { SEO, breadcrumbLd, webPageLd } from "@/lib/seo";

const TITLE = "How it works";
const DESC =
  "How DoppelCrush works in 6 steps: create an account, drop a selfie, choose your vibe (Doppel or Chaos), get matched, chat, and share your reveal with friends in a Doppel Room.";

const steps = [
  { icon: Users,         title: "Create your account",  body: "Free signup with email. 18+ only. Pick a name and you're in." },
  { icon: Camera,        title: "Drop your selfie",     body: "We use your selfie to build a face card. Best results: one face, well-lit, looking at the camera. Your photo stays private." },
  { icon: Heart,         title: "Pick your preferences", body: "Match with women, men or everyone. Pick Doppel Mode (twin energy) or Chaos Mode (plot twist energy). Switch any time." },
  { icon: Zap,           title: "Get your matches",     body: "Our ranker scores faces by similarity (Doppel) or contrast (Chaos), then surfaces the best picks for you." },
  { icon: MessageCircle, title: "Like, match, chat",    body: "Tap the heart on faces you're into. Mutual ♥ unlocks a chat with real-time messaging." },
  { icon: Share2,        title: "Share with friends",   body: "Open a Doppel Room and drop the link in your group chat. Each friend gets their OWN reveal and we crown the strongest twin, the wildest chaos pick and the funniest reveal." },
];

export default function HowItWorks() {
  return (
    <div className="crush-bg min-h-screen" data-testid="how-page">
      <SEO
        title={TITLE}
        description={DESC}
        path="/how-it-works"
        jsonLd={[
          webPageLd({ title: TITLE, description: DESC, path: "/how-it-works" }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "How it works", path: "/how-it-works" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: "How to use DoppelCrush",
            description: DESC,
            step: steps.map((s, i) => ({
              "@type": "HowToStep",
              position: i + 1,
              name: s.title,
              text: s.body,
            })),
          },
        ]}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-16">
        <Header />
        <div className="crush-frame mt-4 p-6 sm:p-10 relative">
          <Sticker kind="spark" size={64} className="absolute -top-6 -right-4 rotate-12" />
          <h1 className="font-display text-5xl font-bold text-slate-900">How DoppelCrush works</h1>
          <p className="mt-3 text-slate-600 font-body max-w-2xl text-lg">
            Six steps. No essays. No biometric mumbo-jumbo. Just face card energy and a way better group-chat moment.
          </p>

          <ol className="mt-8 grid sm:grid-cols-2 gap-4">
            {steps.map((s, i) => (
              <li key={s.title} className="bg-white rounded-3xl p-6 border-2 border-pink-100 shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-pink-100 text-pink-600 grid place-items-center"><s.icon className="w-5 h-5" /></div>
                  <div className="font-display font-bold text-pink-600">Step {i + 1}</div>
                </div>
                <h2 className="font-display text-2xl font-bold text-slate-900 mt-2">{s.title}</h2>
                <p className="font-body text-slate-600 mt-1">{s.body}</p>
              </li>
            ))}
          </ol>

          <div className="mt-10">
            <h2 className="font-display text-3xl font-bold">The two modes</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div className="rounded-2xl border-2 border-pink-100 bg-pink-50/50 p-5">
                <div className="inline-flex items-center gap-2 font-display font-bold text-pink-700">
                  <Heart className="w-4 h-4" /> Doppel Mode
                </div>
                <p className="text-sm font-body text-slate-700 mt-2">
                  Twin energy. The ranker scores faces by similarity to yours. The more you look alike, the higher they show up.
                </p>
              </div>
              <div className="rounded-2xl border-2 border-purple-100 bg-purple-50/50 p-5">
                <div className="inline-flex items-center gap-2 font-display font-bold text-purple-700">
                  <Zap className="w-4 h-4" /> Chaos Mode
                </div>
                <p className="text-sm font-body text-slate-700 mt-2">
                  Plot twist energy. We surface people who land in the &quot;deliberately unlike you&quot; contrast band. Way more interesting first dates.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/auth" className="crush-cta rounded-full font-display font-bold px-6 py-3 inline-flex" data-testid="how-signup-cta">Get started</Link>
            <Link to="/safety" className="crush-secondary rounded-full font-display font-bold px-6 py-3">Safety →</Link>
            <Link to="/faq" className="crush-secondary rounded-full font-display font-bold px-6 py-3">FAQ →</Link>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
